import React, { useState } from "react";
import { FaList, FaTrash } from "react-icons/fa6";
import LibraryModal from "../../../../../components/LibraryModal";

// Import dnd-kit
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

function ensureArray(val) {
  if (Array.isArray(val)) return val;
  if (!val) return [];
  try {
    const arr = typeof val === "string" ? JSON.parse(val) : val;
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

const DEFAULT_TESTIMONIAL = {
  src: "",
  name: "",
  social: "",
  feedback: "",
  stars: 5,
};

function SortableTestimonialItem({
  id,
  t,
  idx,
  handleTestimonialChange,
  handleRemoveTestimonial,
  handleImageClick,
  handleRemoveImage,
  testimonialsLength,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : "auto",
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className="flex flex-row items-start gap-4 border border-gray-200 rounded-lg p-4 mb-2 bg-white shadow-sm relative"
    >
      {/* Grab handle */}
      <div className="flex flex-col items-center justify-start pt-2 pr-1">
        <span
          className="w-7 h-7 rounded flex items-center justify-center bg-gray-100 hover:bg-gray-200 transition"
          style={{
            overflow: "hidden",
            minWidth: "28px",
            minHeight: "28px",
            cursor: "grab",
          }}
          title="Arraste para reordenar"
          tabIndex={-1}
          {...listeners}
        >
          <FaList className="text-gray-500 text-lg" />
        </span>
      </div>

      {/* Image */}
      <div className="flex flex-col items-center justify-start gap-1 relative">
        <div
          className="w-14 h-14 rounded-full cursor-pointer border-2 border-dashed border-gray-300 flex items-center justify-center bg-gray-50 hover:bg-gray-100 transition"
          style={{
            overflow: "hidden",
            minWidth: "56px",
            minHeight: "56px",
          }}
          onClick={() => handleImageClick(idx)}
          title="Clique para escolher imagem"
        >
          {t.src ? (
            t.src.includes("http") ? (
              <img
                src={t.src}
                alt={t.name}
                className="w-14 h-14 rounded-full object-cover"
              />
            ) : (
              <span className="text-gray-400 text-2xl">{t.src}</span>
            )
          ) : (
            <span className="text-gray-400 text-xs">Img</span>
          )}
        </div>
        {t.src ? (
          <div
            className="absolute top-0 right-0 cursor-pointer bg-white rounded-full p-1 shadow"
            onClick={() => handleRemoveImage(idx)}
            title="Remover imagem"
            style={{ transform: "translate(40%, -40%)" }}
          >
            <FaTrash className="text-red-500 text-xs" />
          </div>
        ) : null}
      </div>

      {/* Main content: name, social, feedback, stars */}
      <div className="flex flex-col flex-1 gap-2">
        <div className="flex flex-row gap-2 items-center">
          <input
            type="text"
            className="input input-bordered input-sm flex-1"
            value={t.name}
            onChange={(e) =>
              handleTestimonialChange(idx, "name", e.target.value)
            }
            placeholder="Nome"
          />
          <button
            className="ml-2 text-red-500 hover:text-red-700 text-lg font-bold px-2 py-0.5 rounded transition absolute top-2 right-2"
            onClick={() => handleRemoveTestimonial(idx)}
            title="Remover testemunho"
            type="button"
            disabled={testimonialsLength <= 1}
            style={{ lineHeight: 1 }}
          >
            &times;
          </button>
        </div>
        <input
          type="text"
          className="input input-bordered input-sm"
          value={t.social}
          onChange={(e) =>
            handleTestimonialChange(idx, "social", e.target.value)
          }
          placeholder="@usuario"
        />
        <textarea
          className="input input-bordered input-sm"
          value={t.feedback}
          onChange={(e) =>
            handleTestimonialChange(idx, "feedback", e.target.value)
          }
          placeholder="Comentário"
          rows={2}
        />
        <div className="flex flex-row items-center gap-2 mt-1">
          <label className="text-xs font-semibold text-gray-600">Estrelas</label>
          <input
            type="number"
            min={1}
            max={5}
            className="input input-bordered input-sm w-20"
            value={t.stars}
            onChange={(e) =>
              handleTestimonialChange(
                idx,
                "stars",
                Math.max(1, Math.min(5, Number(e.target.value) || 1))
              )
            }
          />
        </div>
      </div>
    </div>
  );
}

const ConfigTestimonial = ({ block, updateBlock }) => {
  // Use block.data?.testimonials, fallback to default
 
    let testimonials = block.testimonials;

  const [open, setOpen] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(null);

  // DnD-kit sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  const handleTestimonialChange = (idx, field, value) => {
    const updated = testimonials.map((t, i) =>
      i === idx ? { ...t, [field]: value } : t
    );
    updateBlock(block.id, 'testimonials', updated);
  };

  const handleAddTestimonial = () => {
    const updated = [
      ...testimonials,
      { ...DEFAULT_TESTIMONIAL },
    ];
    updateBlock(block.id, 'testimonials', updated);
  };

  const handleRemoveTestimonial = (idx) => {
    if (testimonials.length <= 1) return;
    const updated = testimonials.filter((_, i) => i !== idx);
    updateBlock(block.id, 'testimonials', updated);
  };

  const handleImageClick = (idx) => {
    setSelectedIdx(idx);
    setOpen(true);
  };

  const handleImageSelect = (image) => {
    if (selectedIdx === null) return;
    const url = image.url || image.src || image;
    handleTestimonialChange(selectedIdx, "src", url);
    setOpen(false);
    setSelectedIdx(null);
  };

  const handleRemoveImage = (idx) => {
    handleTestimonialChange(idx, "src", "");
  };

  // DnD-kit: handle drag end
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = testimonials.findIndex((_, i) => i === active.id);
    const newIndex = testimonials.findIndex((_, i) => i === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const updated = arrayMove(testimonials, oldIndex, newIndex);
    updateBlock(block.id, 'testimonials', updated);
  };
  

  // Use indices as ids for sortable context
  const itemIds = testimonials.map((_, idx) => idx);

  return (
    <div className="flex flex-col gap-4">
      <div className="font-bold text-lg mb-2">Testemunhos</div>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
          {testimonials.map((t, idx) => (
            <SortableTestimonialItem
              key={idx}
              id={idx}
              t={t}
              idx={idx}
              handleTestimonialChange={handleTestimonialChange}
              handleRemoveTestimonial={handleRemoveTestimonial}
              handleImageClick={handleImageClick}
              handleRemoveImage={handleRemoveImage}
              testimonialsLength={testimonials.length}
            />
          ))}
        </SortableContext>
      </DndContext>
      <button
        className="bg-blue-500 text-white rounded px-3 py-1 text-sm hover:bg-blue-600 w-fit"
        onClick={handleAddTestimonial}
        type="button"
      >
        + Adicionar Depoimento
      </button>

      <div className="flex flex-row items-center gap-2">
        <label className="text-sm text-gray-500">Estilo</label>
        <select className="input input-bordered input-sm" value={block.style} onChange={(e) => updateBlock(block.id, 'style', e.target.value)}>
          <option value="list">Lista</option>
          <option value="grid">Grade</option>
        </select>
      </div>
      <LibraryModal
        open={open}
        onClose={() => {
          setOpen(false);
          setSelectedIdx(null);
        }}
        onSelect={handleImageSelect}
      />
    </div>
  );
};

export default ConfigTestimonial;